import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.ormuz-subida-precios-hosteleria-otono-2026.${f}`

const REF = {
    vozpopuli: {
        href: 'https://www.vozpopuli.com/economia/macroeconomia/el-precio-de-los-alimentos-se-disparara-un-7-tras-el-verano-por-el-impacto-de-los-fertilizantes.html',
        domain: 'vozpopuli.com',
        label: 'Funcas · Vozpópuli',
    },
    interempresas: {
        href: 'https://www.interempresas.net/Grandes-cultivos/627363-Espana-esquiva-la-ruta-de-fertilizantes-por-el-Estrecho-de-Ormuz-pero-no-el-alza-de-precio.html',
        domain: 'interempresas.net',
        label: 'FAO y Anffe · Interempresas',
    },
    unctad: {
        href: 'https://mascontainer.com/unctad-advierte-que-golpe-a-alimentos-y-paises-vulnerables-seguira-sintiendose-pese-a-pseudo-apertura-de-ormuz/',
        domain: 'mascontainer.com',
        label: 'UNCTAD · MasContainer',
    },
    infobae: {
        href: 'https://www.infobae.com/america/mundo/2026/07/29/iran-propuso-a-oman-una-formula-de-trafico-compartido-para-reabrir-el-estrecho-de-ormuz/',
        domain: 'infobae.com',
        label: 'Infobae',
    },
    elespanol: {
        href: 'https://www.elespanol.com/invertia/economia/macroeconomia/20260526/ormuz-ahoga-costes-comercio-mundial-precio-transportar-mercancias-barco-duplica-solo-meses/1003744258320_0.html',
        domain: 'elespanol.com',
        label: 'El Español',
    },
    roams: {
        href: 'https://roams.es/actualidad/energia/precio-luz-nivel-alto-tres-150-mwh-causas/',
        domain: 'roams.es',
        label: 'Roams',
    },
    ocu: {
        href: 'https://www.ocu.org/consumo-familia/supermercados/informe/evolucion-precios-cesta-compra',
        domain: 'ocu.org',
        label: 'OCU',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function OrmuzSubidaPreciosArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de Ormuz y la subida de precios en hostelería y me gustaría revisar el escandallo de mi plato estrella.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            <strong>
                {t(
                    k('weekActions.1.strong'),
                    'Coge tus cinco platos más vendidos y vuelve a hacer el escandallo con los precios de hoy.',
                )}
            </strong>
            {t(
                k('weekActions.1.after'),
                ' De hoy, no los del año pasado. Si nunca lo has hecho, empieza por el que más sale.',
            )}
        </>,
        <>
            <strong>
                {t(
                    k('weekActions.2.strong'),
                    'Llama a tus tres proveedores grandes y pregúntales qué van a hacer en septiembre.',
                )}
            </strong>
            {t(k('weekActions.2.after'), ' El que te lo diga te está regalando dos meses.')}
        </>,
        <>
            <strong>
                {t(
                    k('weekActions.3.strong'),
                    'Mira los albaranes de los últimos tres meses y busca el recargo de combustible.',
                )}
            </strong>
            {t(k('weekActions.3.after'), ' Si ha aparecido o ha crecido, ya estás pagando Ormuz sin saberlo.')}
        </>,
        <>
            <strong>
                {t(k('weekActions.4.strong'), 'Mira en qué platos puedes cambiar un producto de fuera por uno de aquí')}
            </strong>
            {t(k('weekActions.4.after'), ' sin que el cliente lo note.')}
        </>,
        <>
            <strong>{t(k('weekActions.5.strong'), 'Decide ya cuándo vas a subir la carta.')}</strong>
            {t(
                k('weekActions.5.after'),
                ' Con un número: «si este plato me pasa del 33%, subo». En octubre y con prisa se decide mal.',
            )}
        </>,
    ]

    const sources = [
        {
            label: t(k('sources.1'), 'Funcas, previsiones económicas (mayo 2026), vía Vozpópuli'),
            href: REF.vozpopuli.href,
        },
        { label: t(k('sources.2'), 'FAO y Anffe, vía Interempresas / Efeagro'), href: REF.interempresas.href },
        {
            label: t(
                k('sources.3'),
                'UNCTAD sobre el impacto prolongado en alimentos y transporte (9 de julio de 2026)',
            ),
            href: REF.unctad.href,
        },
        {
            label: t(
                k('sources.4'),
                'Irán propone a Omán repartirse el tráfico del estrecho, Infobae, 29 de julio de 2026',
            ),
            href: REF.infobae.href,
        },
        {
            label: t(k('sources.5'), 'Ormuz duplica el coste del transporte marítimo mundial, El Español'),
            href: REF.elespanol.href,
        },
        {
            label: t(k('sources.6'), 'El precio de la luz marca su nivel más alto en tres años, Roams'),
            href: REF.roams.href,
        },
        { label: t(k('sources.7'), 'Evolución de la cesta de la compra, julio 2026, OCU'), href: REF.ocu.href },
        {
            label: t(k('sources.anuario'), 'Anuario de la Hostelería de España (más de 300.000 establecimientos)'),
            href: REF.anuario.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.before7'), 'Funcas prevé que la comida suba un ')}
                            <strong>7%</strong>
                            {t(k('dek.after7Before6'), ' después del verano por el bloqueo de Ormuz. El abono tarda ')}
                            <strong>{t(k('dek.months'), '6 meses')}</strong>
                            {t(k('dek.after6'), ' en llegar a tu albarán: te cae en octubre.')}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Albarán y caja de verdura de temporada sobre una encimera de cocina profesional',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        {t(
                            k('intro.1'),
                            'Ormuz lleva cerrado a ratos desde febrero. Por ahí pasa uno de cada cinco barriles de petróleo y gas y un tercio del abono agrícola. La luz está en máximos de tres años. Funcas calcula que la comida subirá cerca de un 7% después del verano. El abono tarda seis meses en aparecer en tu albarán.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('intro.2'),
                            'Julio ha ido bien: terraza llena, tomate un poco más caro y la factura de la luz aplazada a septiembre. Eso todavía no es Ormuz. ',
                        )}
                        <strong>{t(k('intro.5'), 'Ormuz te llega cuando el dinero del verano ya se haya ido.')}</strong>
                    </p>
                </div>

                <section id='en-cristiano'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.enCristiano'), 'Ormuz, el 7% de Funcas y por qué te pega en otoño')}
                    </h2>
                    <div className={article.tableWrap}>
                        <table className={article.tableFacts}>
                            <tbody>
                                <tr>
                                    <th scope='row'>{t(k('table.whatHappened.label'), '¿Qué ha pasado?')}</th>
                                    <td>
                                        {t(
                                            k('table.whatHappened.value'),
                                            'Ormuz cerrado a ratos desde el 28 de febrero de 2026',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.whyMe.label'), '¿Y a mí qué?')}</th>
                                    <td>
                                        {t(
                                            k('table.whyMe.value'),
                                            'Por ahí pasa el 20% del petróleo y el gas del mundo, y un tercio del abono',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.foodRise.label'), '¿Cuánto sube la comida?')}</th>
                                    <td>
                                        <span className={article.cite}>
                                            {t(k('table.foodRise.before'), 'Cerca de un ')}
                                            <strong>7%</strong>
                                            {t(k('table.foodRise.after'), ', según Funcas')}
                                            <SourceChip source='vozpopuli' />
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.whenFeel.label'), '¿Cuándo lo noto?')}</th>
                                    <td>
                                        <strong>
                                            {t(k('table.whenFeel.strong'), 'Entre finales de agosto y octubre.')}
                                        </strong>
                                        {t(k('table.whenFeel.after'), ' No ahora')}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.electricity.label'), '¿Y la luz?')}</th>
                                    <td>
                                        <span className={article.cite}>
                                            {t(
                                                k('table.electricity.text'),
                                                'Ya está subiendo: en máximos de tres años',
                                            )}
                                            <SourceChip source='roams' />
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section id='guerra-y-tomates'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.guerraTomates'), 'Qué tiene que ver una guerra con tu caja de tomates')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('guerra.p1.before'), 'El gas no solo enciende tu plancha. ')}
                            <strong>
                                {t(
                                    k('guerra.p1.strong'),
                                    'Con gas se fabrica el abono que el agricultor echa al campo.',
                                )}
                            </strong>
                        </p>
                        <p className={article.p}>
                            {t(
                                k('guerra.p2'),
                                'Sube el gas → sube el abono → sube lo que se siembra → medio año después, sube tu albarán.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('guerra.p3'),
                                'Ahí está la trampa. La luz la notas el mes que viene. La comida la notas seis meses tarde.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='detalle-que-casi-nadie-cuenta'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.detalle'), 'España no importa abono por Ormuz (y da igual)')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(k('detalle.p1.before'), 'España casi no compra abono que pase por Ormuz.')}
                                <SourceChip source='interempresas' />
                            </span>{' '}
                            {t(
                                k('detalle.p1.after'),
                                'La patronal del sector dice que el nuestro viene de Egipto, Argelia y Rusia.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('detalle.p2'),
                                'Da igual. El abono se paga a precio mundial, y el gas con el que se hace, también. Nos libramos del barco, no de la factura.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('detalle.p3.before'), 'La ')}
                            <span className={article.cite}>
                                {t(k('detalle.p3.cite'), 'FAO calcula que el abono subirá entre un 15% y un 20%')}
                                <SourceChip source='interempresas' />
                            </span>{' '}
                            {t(k('detalle.p3.after'), 'este año.')}
                        </p>
                    </div>
                </section>

                <aside
                    className={article.breakout}
                    aria-label={t(k('breakout.calendar.aria'), 'El calendario del golpe')}
                >
                    <div className={article.breakCopy}>
                        <p className={article.breakEyebrow}>{t(k('breakout.eyebrow'), 'El calendario del golpe')}</p>
                        <p className={article.breakLead}>
                            {t(
                                k('breakout.lead'),
                                'La luz ya está en la factura. La comida llega cuando el verano ya se ha ido.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('breakout.p'),
                                'Finales de agosto, primer aviso. Finales de octubre, el golpe de verdad.',
                            )}
                        </p>
                    </div>
                </aside>

                <section id='cuando-se-ve'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuandoSeVe'), '¿Cuándo se ve la subida de precios en tu caja?')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('cuando.p2.beforeFeb'), 'Empezó el ')}
                            <strong>{t(k('cuando.dateFeb'), '28 de febrero')}</strong>
                            {t(k('cuando.p2.before6'), '. Del abono al plato pasan unos ')}
                            <strong>{t(k('cuando.months'), 'seis meses')}</strong>
                            {t(k('cuando.p2.after'), '.')}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('cuando.p3'), 'Febrero + 6 = finales de agosto (primer aviso).')}</strong>{' '}
                            {t(k('cuando.p4.before'), 'A finales de abril el abono ya iba un ')}
                            <strong className={article.cite}>
                                {t(k('cuando.p4.strong'), '50% más caro')}
                                <SourceChip source='vozpopuli' />
                            </strong>
                            {t(k('cuando.p5'), '. Abril + 6 = finales de octubre (el golpe).')}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('cuando.p6'),
                                'Septiembre te llega tocado y octubre entero: justo cuando la caja del verano se ha ido en nóminas, autónomos e IVA.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='primero-lo-malo'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>{t(k('h2.primeroLoMalo'), 'Primero lo malo')}</h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong>{t(k('malo.p2.strong'), 'La carta ya no da más de sí.')}</strong>
                            {t(
                                k('malo.p2.after'),
                                ' Un tercio de las cadenas admite que subir precios fue su palanca y que ya no pueden más sin perder clientes.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>
                                {t(k('malo.p3.strong'), 'Los precios suben en ascensor y bajan por la escalera.')}
                            </strong>
                            {t(
                                k('malo.p3.after'),
                                ' Aunque Ormuz abra mañana, tu proveedor no te lo bajará a la misma velocidad. La ',
                            )}
                            <span className={article.cite}>
                                {t(k('malo.p4.cite'), 'UNCTAD')}
                                <SourceChip source='unctad' />
                            </span>{' '}
                            {t(
                                k('malo.p4.after'),
                                'avisa: comida y transporte tardan más en recuperarse que la energía.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <strong>{t(k('malo.p5.strong'), 'Lo bueno: esta vez lo ves venir.')}</strong>
                            {t(k('malo.p5.after'), ' Tienes dos meses. En 2022 no los tuvo nadie.')}
                        </p>
                    </div>
                </section>

                <section id='que-productos'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.queProductos'), 'Qué productos vigilar (y cuáles no)')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGapMd)}>{t(k('productos.intro'), 'Cuatro caminos:')}</p>
                        <p className={article.p}>
                            <strong>
                                {t(k('productos.1.strong'), '1. Por el abono. Llega en septiembre y octubre.')}
                            </strong>
                            {t(
                                k('productos.1.after'),
                                ' Verdura, patata, legumbre y cereal. Y todo lo que come pienso: pollo, cerdo, huevo y leche. En marzo los huevos ya iban un 21% arriba y las legumbres un 19%.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('productos.2.strong'), '2. Por la energía. Ya está aquí.')}</strong>
                            {t(
                                k('productos.2.after'),
                                ' Todo lo que pasa por una cámara o por un horno antes de llegarte: congelado, lácteo, bollería, conserva. Y tu propia cocina.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>
                                {t(k('productos.3.strong'), '3. Por el barco. Cuando te toque renovar precios.')}
                            </strong>
                            {t(
                                k('productos.3.before'),
                                ' Lo que viene de fuera de Europa: arroz, langostino y pota congelados, café, especias. Y el envase, que casi nadie mete en el escandallo: el film, la bandeja y el táper salen del petróleo. Traer un contenedor ha llegado a ',
                            )}
                            <span className={article.cite}>
                                {t(k('productos.3.cite'), 'costar el doble en tres meses')}
                                <SourceChip source='elespanol' />
                            </span>
                            .
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('productos.4.strong'), '4. Por el gasóleo. Ya te lo están pasando.')}</strong>
                            {t(
                                k('productos.4.after'),
                                ' El reparto. O te lo ponen como recargo o te lo esconden dentro del precio.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('productos.outro'),
                                'Lo que menos se mueve: fresco de aquí y de temporada. Lleva menos barco y menos frío encima.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuenta-con-numeros'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuentaNumeros'), 'La cuenta con números')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('numeros.p1.before'), 'Un bar que factura ')}
                            <strong>40.000 € al mes</strong>
                            {t(k('numeros.p1.after'), '.')}
                        </p>
                        <p className={cn(article.pNote, article.pGapMd)}>
                            {t(
                                k('numeros.note'),
                                'Números de ejemplo, estimación propia a partir de una estructura de costes habitual; cambian mucho de un local a otro.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(k('numeros.todayLabel'), 'Lo que se gasta hoy:')}
                        </p>
                        <ul className={article.dashList}>
                            <li className={article.dashItem}>
                                {t(k('numeros.today.1'), 'En comida y bebida: 12.800 € (el 32%)')}
                            </li>
                            <li className={article.dashItem}>
                                {t(k('numeros.today.2'), 'En luz y gas: 1.600 € (el 4%)')}
                            </li>
                            <li className={article.dashItem}>{t(k('numeros.today.3'), 'Le queda limpio: 3.200 €')}</li>
                        </ul>
                        <p className={cn(article.p, article.pGapMd)}>
                            {t(
                                k('numeros.autumnLabel'),
                                'Lo que se gastará en otoño, con ese 7% en la comida y un 20% más de luz:',
                            )}
                        </p>
                        <ul className={article.dashList}>
                            <li className={article.dashItem}>{t(k('numeros.autumn.1'), 'Comida: +896 €')}</li>
                            <li className={article.dashItem}>{t(k('numeros.autumn.2'), 'Luz y gas: +320 €')}</li>
                            <li className={article.dashItem}>
                                {t(k('numeros.autumn.3'), 'Total: 1.216 € más cada mes')}
                            </li>
                        </ul>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('numeros.p2'),
                                'Más de un tercio de lo limpio. Para taparlo solo con la carta: +3% (café de 1,60 € a 1,65 €; menú de 14 € a 14,45 €). Si tu cliente lo aguanta en octubre, eso ya lo sabes tú.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Qué mirar en albarán y carta antes de octubre')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), 'Si el 7% se queda corto, si Ormuz abre y si prorrogan ayudas')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong className={article.cite}>
                                {t(k('pendientes.p1.strong'), 'Ese 7% puede quedarse corto.')}
                                <SourceChip source='vozpopuli' />
                            </strong>{' '}
                            {t(
                                k('pendientes.p1.after'),
                                'Funcas lo calculó el 6 de mayo dando por hecho que la guerra acabaría en un mes. No acabó. Ellos mismos manejan otro escenario, con el bloqueo largo, en el que todo sube más.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('pendientes.p2.strong'), 'Nadie sabe si Ormuz abre.')}</strong>
                            {t(k('pendientes.p2.before'), ' El 29 de julio Irán ')}
                            <span className={article.cite}>
                                {t(k('pendientes.p2.cite'), 'propuso a Omán repartirse el tráfico del estrecho')}
                                <SourceChip source='infobae' />
                            </span>
                            . {t(k('pendientes.p2.after'), 'No hay nada firmado.')}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <strong>
                                {t(k('pendientes.p3.strong'), 'Las ayudas del Gobierno son una incógnita.')}
                            </strong>
                            {t(
                                k('pendientes.p3.after'),
                                ' Las cuentas de Funcas dan por hecho que las medidas duran hasta octubre, pero el IVA de la luz ya volvió al 21% en junio. Si no prorrogan nada más, la factura de otoño será peor de lo previsto.',
                            )}
                        </p>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('closing.p1'),
                            'Si esta semana miras un solo número, que sea el escandallo de tu plato estrella con precios de hoy. Si ya no da, escríbenos y le ponemos números antes de octubre.',
                        )}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Tu plato estrella sigue dando margen?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Recalcula el escandallo con precios de hoy y te ayudamos a ver qué margen real te queda antes de octubre.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
